import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function AccountabilityInvestigate() {
  const [form, setForm] = useState({ name: "", email: "", municipality: "", topic: "", details: "" });
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name.length >= 2 && /.+@.+\..+/.test(form.email) && form.municipality.length >= 2 && form.topic.length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/accountability/requests", { ...form, details: form.details || null, website: hp });
      setDone(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-xl mx-auto"><CardContent className="p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="font-heading font-bold text-xl" data-testid="accountability-request-success">Request received</h1>
          <p className="text-sm text-muted-foreground mt-3">{done}</p>
        </CardContent></Card>
      </div>
    );

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="accountability-investigate-title">
          Investigate my municipality
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Suggest a factual, public-record question about your municipality's spending — technology contracts,
          procurement, capital projects or ongoing costs. We research using public documents only.
        </p>
      </div>
      <form onSubmit={submit} className="mt-8 max-w-xl">
        <Card><CardContent className="p-6 sm:p-8 space-y-5">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="acct-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="acct-email" />
            </div>
          </div>
          <div>
            <Label>Municipality *</Label>
            <Input className="mt-1.5" value={form.municipality} onChange={(e) => set("municipality", e.target.value)} data-testid="acct-municipality" />
          </div>
          <div>
            <Label>Topic *</Label>
            <Input className="mt-1.5" placeholder="e.g. Annual software licensing costs" value={form.topic} onChange={(e) => set("topic", e.target.value)} data-testid="acct-topic" />
          </div>
          <div>
            <Label>What should we look at? (optional)</Label>
            <Textarea className="mt-1.5" rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} data-testid="acct-details" />
          </div>
          <Button type="submit" className="w-full h-11" disabled={!valid || busy} data-testid="acct-submit">
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </CardContent></Card>
      </form>
    </div>
  );
}
